<template>
  <div class="task-upload-modal-root">
  <lb-dialog
    v-model="drawerVisible"
    :heading="uploadModalHeading"
    content-class="task-upload-modal-dialog"
    :loading="isUploadLoading"
    data-testid="task-upload-modal"
  >
    <template v-slot:body>
      <div class="task-upload-modal__body" data-testid="task-upload-modal__body">
        <div v-if="uploadLimitExceeded" class="task-upload-modal__limit-error" data-testid="task-upload-modal__limit-error">
          <div class="task-upload-modal__limit-icon-wrap">
            <v-icon class="task-upload-modal__limit-icon" size="24">mdi-alert-outline</v-icon>
          </div>
          <h3 class="task-upload-modal__limit-title">Upload Limit Exceeded</h3>
          <p class="task-upload-modal__limit-message">
            You can only upload a maximum of {{ MAX_FILES_PER_UPLOAD }} files at a time. Please reduce the number of files and try again.
          </p>
          <button
            type="button"
            class="task-upload-modal__retry-btn"
            data-testid="task-upload-modal__retry-btn"
            @click="clearUploadLimitError"
          >
            Retry
          </button>
        </div>

        <template v-else>
          <section v-if="allowedTaskTypes" class="task-upload-modal__step-block" data-testid="task-upload-modal__step-1">
            <h5 class="task-upload-modal__step-title">Step 1: Download Template</h5>
            <p class="task-upload-modal__step-desc">
              Start by downloading the standard Excel template containing your current task data.
            </p>
            <button
              type="button"
              class="task-upload-modal__download-link"
              data-testid="task-upload-modal__download-template-btn"
              @click="$nova.downloadFile('', {}, '/v2/financialclose/analysis/gettasklisttemplateV3')"
            >
              <img src="/img/icons/DownloadIcon.svg" alt="" width="16" height="16" />
              Download template
            </button>
          </section>

          <section class="task-upload-modal__step-block task-upload-modal__step-block--upload" data-testid="task-upload-modal__step-2">
            <h5 class="task-upload-modal__step-title">{{ uploadStepHeading }}</h5>
            <span class="task-upload-modal__step-desc">
              Save your changes in the Excel file, then drag and drop it here to update your tasks in the system
            </span>

            <div
              v-if="showBulkEvidenceRequiredBanner"
              class="task-upload-modal__evidence-banner"
              data-testid="task-upload-modal__bulk-evidence-required-banner"
            >
              <v-icon small color="#eba937">mdi-alert-outline</v-icon>
              Evidence required : {{ evidenceRequiredCount }} Files
            </div>

            <div
              v-if="bulkEvidenceWarningMessage"
              class="task-upload-modal__evidence-warning"
              data-testid="task-upload-modal__bulk-evidence-warning"
            >
              <v-icon small color="#eba937">mdi-alert-outline</v-icon>
              {{ bulkEvidenceWarningMessage }}
            </div>

            <div
              v-if="showEvidenceBanner"
              class="task-upload-modal__evidence-banner"
              data-testid="task-upload-modal__evidence-banner"
            >
              <v-icon small color="#eba937">mdi-alert-outline</v-icon>
              Evidence required : {{ evidenceRequiredCount }} Files
            </div>

            <div
              v-if="showDropZone"
              class="task-upload-modal__drop-zone"
              :class="{ 'task-upload-modal__drop-zone--dragging': isDragging }"
              data-testid="task-upload-modal__drop-zone"
              @dragover.prevent="onDragOver"
              @dragleave.prevent="onDragLeave"
              @drop.prevent="onDrop"
            >
              <p class="task-upload-modal__drop-text">
                drag a file here (or)
                <button
                  type="button"
                  class="task-upload-modal__drop-link"
                  data-testid="task-upload-modal__browse-btn"
                  @click="openFilePicker"
                >
                  upload a file
                </button>
              </p>
            </div>

            <input
              ref="fileInput"
              type="file"
              class="task-upload-modal__file-input"
              multiple
              data-testid="task-upload-modal__file-input"
              :accept="fileAccept"
              @change="onFileInputChange"
            />

            <TaskUploadFileList
              v-if="localFileuplaoded.length"
              :files="localFileuplaoded"
              data-testid="task-upload-modal__file-list"
              @remove="removeSelectedFile"
            />
          </section>

          <section class="task-upload-modal__remarks" data-testid="task-upload-modal__remarks">
            <span class="task-upload-modal__remarks-label">
              Remarks{{ localUploadWithoutFile ? ' (required)' : '' }}
            </span>
            <textarea
              id="task-upload-remarks"
              v-model="localFileremark"
              class="task-upload-modal__remarks-input"
              rows="4"
              placeholder="Add remarks here"
              data-testid="task-upload-modal__remark-input"
            />
          </section>
        </template>
      </div>
    </template>

    <template v-slot:actions>
      <div class="task-upload-modal__footer" data-testid="task-upload-modal__footer">
        <div class="task-upload-modal__footer-left">
          <v-checkbox
            v-if="showUploadWithoutDocument"
            v-model="localUploadWithoutFile"
            hide-details
            class="ma-0 pa-0"
            data-testid="task-upload-modal__without-doc-checkbox"
          >
            <template #label>
              <span class="task-upload-modal__checkbox-label">Upload without Document</span>
            </template>
          </v-checkbox>
        </div>
        <div class="task-upload-modal__footer-actions">
          <button
            type="button"
            class="task-upload-modal__btn task-upload-modal__btn--secondary"
            data-testid="task-upload-modal__cancel-btn"
            @click="closeModal"
          >
            Cancel
          </button>
          <button
            type="button"
            class="task-upload-modal__btn task-upload-modal__btn--primary"
            :data-testid="submitButtonTestId"
            :disabled="isSubmitDisabled || isUploadLoading"
            @click="handlePrimarySubmit"
          >
            {{ submitButtonLabel }}
          </button>
        </div>
      </div>
    </template>
  </lb-dialog>
  </div>
</template>

<script>
import TaskUploadFileList from './TaskUploadFileList.vue';
import './TaskUploadModal.scss';
import { TASK_UPLOAD_LABELS, COMPONENT_EVENTS, BULK_UPLOAD_MESSAGES } from '@/modules/financialclose/utils/constants';
import {
  getMaxEvidenceRequired,
  getMaxAdditionalEvidenceRequired,
  canBulkAutoSubmit,
  getBulkEvidenceMismatchWarning,
  getTaskEvidenceRequired,
  isAccountReconciliationTask,
  isBulkSelectionAccountReconciliation,
} from '@/modules/financialclose/utils/tasklistHelpers';

const MAX_FILES_PER_UPLOAD = 10;

export default {
  name: 'TaskListModel',
  components: {
    TaskUploadFileList,
  },
  props: {
    modelValue: {
      type: Boolean,
      required: false,
      default: false
    },
    bulkworkpaper: Boolean,
    fileuplaoded: Array,
    resetFlag: Boolean,
    fileremark: String,
    uploadWithoutFile: Boolean,
    viewtaskdata: Object,
    fileNamesList: Array,
    filesOnly: {
      type: Boolean,
      default: false
    },
    /**
     * Task details keeps Upload and Submit separate.
     * Task list upload and bulk upload still submit a checklist when evidence is met.
     */
    autoSubmitOnUpload: {
      type: Boolean,
      default: true,
    },
    id: {
      type: [String, Number],
      required: true
    },
    task: {
      type: Object,
      required: true
    },
    // For bulk mode: pass the currently selected tasks from parent (array of task objects)
    selectedTasks: {
      type: Array,
      default: () => []
    },
    pageType: {
      type: String,
      default: ''
    },
    redesign: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  // Add event emits: bulk-upload-complete, clear-selected
  // (update:modelValue already present)
  data() {
    return {
      modifiedname: 'Modified File Name', // Placeholder for modified file name
      fileprocessing: {},
      files: {},
      filesbulk: {},
      sendforreviewloading: false,
      sendforreviewloadingbulk: false,
      submitLoading: false,
      bulkfilesloading: 0,
      clearuploadfiledata: false,
      sendForReviewDialogbulk: false,
      bulkworkpaperupload: false,
      log: 0,
      loading: false,
      localFileuplaoded: this.fileuplaoded || [],
      file: null,
      dataArray: [],
      selectItem: [],
      checklist: {},
      // File upload restrictions (extensions and size)
      ALLOWED_EXTENSIONS: [
        'xlsm', 'xlsx', 'csv', 'xlsb', 'xls', 'pdf', 'txt', 'xml',
        'jpeg', 'jpg', 'png', 'mp4', 'doc', 'docx', 'readme', 'svg', 'ppt', 'pptx'
      ],
      MAX_TOTAL_SIZE: 25 * 1024 * 1024, // 25 MB
      localUploadWithoutFile: this.uploadWithoutFile, // local copy for v-model
      localFileremark: this.fileremark, // local copy for v-model
      localResetFlag: this.resetFlag, // local copy for resetFlag
      MAX_FILES_PER_UPLOAD,
      uploadLimitExceeded: false,
      isDragging: false,
      fileAccept: '.xlsx,.xls,.xlsm,.xlsb,.doc,.docx,.ppt,.pptx,.pdf,.png,.jpg,.jpeg,.svg,.mp4,.csv',
    };
  },
  watch: {
    selectedTasks: {
      handler(newVal){
        if (Array.isArray(newVal)) {
          this.selectItem = newVal;
        }
      },
      immediate: true,
      deep: false
    },
    fileuplaoded(newVal) {
      this.localFileuplaoded = newVal || [];
    },
    drawerVisible(val) {
      if (!val) {
        this.uploadLimitExceeded = false;
        this.isDragging = false;
        // When closing, clear files and the draft remark so the next open starts empty.
        this.localFileuplaoded = [];
        this.localFileuplaoded = [...this.localFileuplaoded];
        this.localFileremark = '';
        this.localUploadWithoutFile = false;
        this.$nextTick(() => {
          this.localResetFlag = !this.localResetFlag;
        });
      }
    },
    resetFlag(newVal) {
      this.localResetFlag = newVal;
    },
    uploadWithoutFile(newVal) {
      this.localUploadWithoutFile = newVal;
    },
    localUploadWithoutFile(newVal) {
      this.$emit(COMPONENT_EVENTS.UPDATE_UPLOAD_WITHOUT_FILE, newVal);
    },
    fileremark(newVal) {
      this.localFileremark = newVal;
    },
    localFileremark(newVal) {
      this.$emit(COMPONENT_EVENTS.UPDATE_FILE_REMARK, newVal);
    },
    showUploadWithoutDocument(visible) {
      if (!visible && this.localUploadWithoutFile) {
        this.localUploadWithoutFile = false;
      }
    }
  },
  computed: {
    allowedTaskTypes() {
      if (this.bulkworkpaper) {
        return isBulkSelectionAccountReconciliation(this.selectItem);
      }
      return isAccountReconciliationTask(this.task);
    },
    uploadStepHeading() {
      return this.allowedTaskTypes ? 'Step 2: Upload Updated File' : 'Upload File';
    },
    // True when task is NOT account reconciliation (i.e., Normal Task)
    isNormalTask() {
      return this.task && this.task.task_type !== this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.ACCOUNT_RECONCILIATION;
    },
    taskData() {
      return this.$store.getters['tasks/getCurrentTask'] || {};
    },
    drawerVisible: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit(COMPONENT_EVENTS.UPDATE_MODEL_VALUE, val);
      }
    },
    // Determine when to show "Upload & Submit"
    // Rules added: If no_of_evidence == 0 we still allow Upload & Submit with either a new file OR remark-only (upload without document + remark)
    showUploadAndSubmit() {
      if (this.filesOnly) return false;
      // Restrict Upload & Submit to Normal Tasks only
      const RECON = this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.ACCOUNT_RECONCILIATION;
      if (this.bulkworkpaper) {
        // In bulk mode, all selected tasks must be normal
        const items = Array.isArray(this.selectItem) ? this.selectItem : [];
        if (!items.length) return false;
        if (!items.every(t => t && t.task_type !== RECON)) return false;
      } else {
        if (!this.isNormalTask) return false;
      }
      // NOTE: localFileuplaoded is the data property tracking selected files
      const newCount = Array.isArray(this.localFileuplaoded) ? this.localFileuplaoded.length : 0;
      const isRemarkOnly = this.localUploadWithoutFile === true;
      const hasRemark = !!(this.localFileremark && this.localFileremark.trim().length);

      // Bulk mode
      if (this.bulkworkpaper) {
        if (!Array.isArray(this.selectItem) || this.selectItem.length === 0) return false;
        const tasks = this.selectItem.filter(t => t); // valid tasks
        if (!tasks.length) return false;
          // Separate tasks with positive requirement and zero requirement
          const positiveReq = tasks.filter((t) => getTaskEvidenceRequired(t) > 0);
          // For tasks with positive requirement ensure satisfied (existing + new >= required)
          const positivesSatisfied = positiveReq.every((t) => {
          const existing = Array.isArray(t.upload_v2) ? t.upload_v2.length : 0;
          return (existing + newCount) >= getTaskEvidenceRequired(t);
        });
        if (!positivesSatisfied) return false;
        // Require at least one new file for positive requirement satisfaction; zero-requirement tasks can still use remark-only.
        const trigger = (newCount > 0) || (isRemarkOnly && positiveReq.length === 0);
        return trigger;
      }

      // Single task
      const required = getTaskEvidenceRequired(this.task);
      const existingCount = Array.isArray(this.task?.upload_v2) ? this.task.upload_v2.length : 0;
      if (required > 0) {
        // Require at least one newly added file to trigger Upload & Submit for evidence-based tasks
        if (newCount === 0) return false;
        return (existingCount + newCount) >= required && (!isRemarkOnly || hasRemark || newCount > 0);
      } else { // required == 0
        // Show as soon as either a file selected or remark-only chosen (remark text may still be empty, button disabled until filled)
        return (newCount > 0) || isRemarkOnly;
      }
    },
    // Single source for button label
    uploadActionLabel() {
      if (this.redesign) return 'Upload & Update';
      if (this.filesOnly) return TASK_UPLOAD_LABELS.UPLOAD;
      if (this.bulkworkpaper) {
        return this.willBulkSubmit ? TASK_UPLOAD_LABELS.SUBMIT : TASK_UPLOAD_LABELS.SAVE;
      }
      return this.showUploadAndSubmit && this.pageType !== 'task-detail'
        ? TASK_UPLOAD_LABELS.UPLOAD_AND_SUBMIT
        : TASK_UPLOAD_LABELS.UPLOAD;
    },
    uploadModalHeading() {
      if (this.bulkworkpaper) return 'File Bulk Upload';
      if (this.redesign) return 'Upload file';
      return 'Single File Upload';
    },
    isUploadLoading() {
      return this.sendforreviewloading || this.submitLoading || this.sendforreviewloadingbulk;
    },
    submitButtonLabel() {
      if (this.isUploadLoading) return 'Uploading...';
      return this.uploadActionLabel;
    },
    submitButtonTestId() {
      if (this.redesign) return 'task-upload-modal__submit-btn';
      if (this.bulkworkpaper) {
        if (this.localUploadWithoutFile) {
          return this.willBulkSubmit
            ? 'task-upload-modal__bulk-remark-submit-btn'
            : 'task-upload-modal__bulk-remark-save-btn';
        }
        return this.willBulkSubmit
          ? 'task-upload-modal__bulk-submit-btn'
          : 'task-upload-modal__bulk-save-btn';
      }
      return this.localUploadWithoutFile
        ? 'task-upload-modal__single-remark-submit-btn'
        : 'task-upload-modal__single-upload-btn';
    },
    isSubmitDisabled() {
      if (this.uploadLimitExceeded) return true;
      if (this.bulkworkpaper) {
        if (!this.filesOnly && this.localUploadWithoutFile) {
          return !this.localFileremark || !this.localFileremark.trim();
        }
        if (!this.localUploadWithoutFile) {
          return !this.localFileuplaoded.length;
        }
        return false;
      }
      if (this.viewtaskdata && this.viewtaskdata.stage === 3) return true;
      if (this.localUploadWithoutFile) {
        return !this.localFileremark || !this.localFileremark.trim();
      }
      return !this.localFileuplaoded.length;
    },
    showEvidenceBanner() {
      if (this.bulkworkpaper) {
        return false;
      }
      const required = getTaskEvidenceRequired(this.task);
      return required > 0;
    },
    showBulkEvidenceRequiredBanner() {
      if (!this.bulkworkpaper) return false;
      // Hide when every task already satisfies evidence via existing uploads
      if (getMaxAdditionalEvidenceRequired(this.selectItem) <= 0) return false;
      return this.bulkSelectedFileCount === 0;
    },
    evidenceRequiredCount() {
      if (this.bulkworkpaper) {
        return getMaxEvidenceRequired(this.selectItem);
      }
      return getTaskEvidenceRequired(this.task);
    },
    bulkSelectedFileCount() {
      if (this.localUploadWithoutFile) return 0;
      return Array.isArray(this.localFileuplaoded) ? this.localFileuplaoded.length : 0;
    },
    willBulkSubmit() {
      if (!this.bulkworkpaper) return false;
      return canBulkAutoSubmit(this.selectItem, this.bulkSelectedFileCount);
    },
    bulkEvidenceWarningMessage() {
      if (!this.bulkworkpaper || this.localUploadWithoutFile) return '';
      return getBulkEvidenceMismatchWarning(
        this.selectItem,
        this.bulkSelectedFileCount,
        BULK_UPLOAD_MESSAGES.EVIDENCE_NOT_MATCHED
      );
    },
    showDropZone() {
      return true;
    },
    /**
     * Show remark-only upload whenever this dialog has no selected file.
     * Saved evidence on the task does not hide it. A file selected here does.
     */
    showUploadWithoutDocument() {
      if (this.filesOnly) return false;
      const newCount = Array.isArray(this.localFileuplaoded) ? this.localFileuplaoded.length : 0;
      return newCount === 0;
    },
  },
  methods: {
    closeModal() {
      this.drawerVisible = false;
    },
    clearUploadLimitError() {
      this.uploadLimitExceeded = false;
      this.localFileuplaoded = [];
      this.localResetFlag = !this.localResetFlag;
    },
    openFilePicker() {
      this.$refs.fileInput?.click();
    },
    onFileInputChange(event) {
      const files = event?.target?.files;
      if (files?.length) {
        this.FilesUpload(Array.from(files));
      }
      if (event?.target) event.target.value = '';
    },
    onDragOver() {
      this.isDragging = true;
    },
    onDragLeave() {
      this.isDragging = false;
    },
    onDrop(event) {
      this.isDragging = false;
      const files = event?.dataTransfer?.files;
      if (files?.length) {
        this.FilesUpload(Array.from(files));
      }
    },
    removeSelectedFile(index) {
      this.localFileuplaoded = this.localFileuplaoded.filter((_, i) => i !== index);
      this.uploadLimitExceeded = false;
    },
    async handlePrimarySubmit() {
      if (this.bulkworkpaper) {
        if (!this.filesOnly && this.localUploadWithoutFile) {
          await this.sendForReviewBulk();
        } else {
          await this.uploadFilesBulk();
        }
        return;
      }
      if (this.localUploadWithoutFile) {
        await this.sendForReview();
        return;
      }
      await this.handleUploadClick(false);
    },
    // Determine which tasks are eligible for auto-submit based on evidence rules and task type
    getSubmitTargets(tasks, newFilesCount) {
      const RECON = this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.ACCOUNT_RECONCILIATION;
      const list = (Array.isArray(tasks) ? tasks : [tasks]).filter(t => t && t._id);
      return list.filter(taskItem => {
        if (taskItem.stage === 3) return false; // already submitted/closed
        if (taskItem.task_type === RECON) return false; // skip reconciliation tasks
        if (getTaskEvidenceRequired(taskItem) > 0) {
          const existingCount = Array.isArray(taskItem.upload_v2) ? taskItem.upload_v2.length : 0;
          if ((existingCount + newFilesCount) < getTaskEvidenceRequired(taskItem)) return false; // not satisfied yet
        }
        return true;
      });
    },
    // Auto submit a set of targets; returns detailed result object
    async autoSubmitTasks(targets) {
      const submitPromises = targets.map(taskItem => this.$store
        .dispatch('tasks/submitTask', { taskId: taskItem._id, cycleId: taskItem.project, suppressSnackbar: true })
        .then(result => ({ taskId: taskItem._id, status: result?.status || 'error' }))
        .catch(errorObj => ({ taskId: taskItem._id, status: 'error', message: errorObj?.message }))
      );
      return Promise.all(submitPromises);
    },
    finishBulkUploadFlow() {
      this.localFileuplaoded = [];
      this.localFileremark = '';
      this.localUploadWithoutFile = false;
      this.localResetFlag = !this.localResetFlag;
      this.drawerVisible = false;
      this.$emit(COMPONENT_EVENTS.BULK_UPLOAD_COMPLETE);
      this.$emit(COMPONENT_EVENTS.CLEAR_SELECTED);
      this.$emit(COMPONENT_EVENTS.REFRESH);
    },
    finishBulkSaveOnlyFlow({ isRemarkOnly = false } = {}) {
      this.$store.commit(
        'sbSuccess',
        isRemarkOnly ? BULK_UPLOAD_MESSAGES.REMARKS_SAVED : BULK_UPLOAD_MESSAGES.FILES_UPLOADED
      );
      this.finishBulkUploadFlow();
    },
    async handleBulkPostSave(newFilesCount, { isRemarkOnly = false } = {}) {
      const tasks = Array.isArray(this.selectItem) && this.selectItem.length ? this.selectItem : [];
      const targets = this.autoSubmitOnUpload ? this.getSubmitTargets(tasks, newFilesCount) : [];
      const shouldSubmit = targets.length > 0;

      if (!shouldSubmit) {
        this.finishBulkSaveOnlyFlow({ isRemarkOnly });
        return true;
      }
      if (targets.length) {
        const submitResults = await this.autoSubmitTasks(targets);
        const successCount = submitResults.filter((r) => r.status === 'success').length;
        if (successCount > 0) {
          this.$store.commit('sbSuccess', BULK_UPLOAD_MESSAGES.SENT_FOR_REVIEW);
        } else {
          this.$store.commit('sbError', 'Failed to submit tasks for review.');
          this.$emit(COMPONENT_EVENTS.REFRESH);
          return false;
        }
      }

      this.finishBulkUploadFlow();
      return true;
    },
    // Unified post-upload handler for both single and bulk modes
    async afterUploadSuccess({ tasks, newFilesCount, isBulk, attemptAutoSubmit }) {
      const taskArray = Array.isArray(tasks) ? tasks : [tasks];
      if (!attemptAutoSubmit) {
        // For single upload without submit attempt, nothing extra
        return;
      }
      if (isBulk) {
        // Bulk auto-submit logic replicating previous behavior
        const targets = this.getSubmitTargets(taskArray, newFilesCount);
        if (!targets.length) return; // nothing eligible
        const submitResults = await this.autoSubmitTasks(targets);
        const successIds = submitResults.filter(r => r.status === 'success').map(r => r.taskId);
        const failedIds = submitResults.filter(r => r.status !== 'success').map(r => r.taskId);
        if (successIds.length) {
          const successRefs = targets.filter(t => successIds.includes(t._id)).map(t => t.ref || t._id).join(', ');
          this.$store.commit('sbSuccess', `Uploaded & Submitted: ${successRefs}`);
        } else if (!failedIds.length) {
          this.$store.commit('sbSuccess', 'Files uploaded');
        }
        if (failedIds.length) {
          const failedRefs = targets.filter(t => failedIds.includes(t._id)).map(t => t.ref || t._id).join(', ');
            this.$store.commit('sbError', `Submit failed: ${failedRefs}`);
        }
        const submittedIds = new Set(targets.map(t => t._id));
        const notSubmitted = taskArray.filter(t => t && t._id && !submittedIds.has(t._id));
        if (notSubmitted.length) {
          const refs = notSubmitted.map(t => t.ref || t._id).join(', ');
          this.$store.commit('sbWarning', `The following tasks: ${refs} were not submitted due to missing evidences. Please upload the required evidences for each task.`);
        }
      } else {
        // Single task auto-submit: replicate previous flow (existing snackbar from submitTask when suppressSnackbar false)
        const taskObj = taskArray[0];
        const eligible = this.getSubmitTargets(taskObj, newFilesCount);
        if (!eligible.length) return; // not eligible yet
        try {
          await this.$store.dispatch('tasks/submitTask', { taskId: taskObj._id, cycleId: taskObj.project, suppressSnackbar: false });
        } catch (e) { /* error snackbar already shown by action */ }
      }
    },
    async handleSubmitTask() {
      if (this.submitLoading) return; // prevent double clicks
      this.submitLoading = true;
      try {
        const cycleId = this.task.project;
        const taskId = this.task._id;
        if (!cycleId || !taskId) throw new Error('Missing cycle or task ID');
        const response = await this.$store.dispatch('tasks/submitTask', { taskId, cycleId });
        if (response?.status === 'success') {
          // Store action already fired success snackbar
          this.$emit(COMPONENT_EVENTS.TASK_SUBMITTED, response);
          this.$emit(COMPONENT_EVENTS.REFRESH);
        }
        return response;
      } catch (err) {
        // Error snackbar already handled in Vuex action; swallow here
        return { status: 'error', message: err?.message };
      } finally {
        this.submitLoading = false;
      }
    },
    async handleUploadClick(isRemarkOnly){
      const newCount = Array.isArray(this.localFileuplaoded) ? this.localFileuplaoded.length : 0;
      const shouldSubmit = this.autoSubmitOnUpload
        && this.showUploadAndSubmit
        && !(this.task && this.task.stage === 3);
      // Guard: prevent submit path if no new files were selected for evidence-required tasks
      if (shouldSubmit && newCount === 0 && getTaskEvidenceRequired(this.task) > 0) {
        // Just close or notify; choose notify for clarity
        this.$store.commit('sbError', 'Please add file(s) before submitting.');
        return;
      }
      const suppressUploadSnackbar = shouldSubmit; // if we're auto-submitting, suppress upload success toast
      const success = this.filesOnly 
        ? await this.uploadFiles()
        : (isRemarkOnly 
            ? await this.sendForReview() 
            : await this.uploadFilesInternal(suppressUploadSnackbar));
      if (!success) return; // abort if failed
      if (!isRemarkOnly) {
        await this.afterUploadSuccess({ tasks: this.task, newFilesCount: newCount, isBulk: false, attemptAutoSubmit: shouldSubmit });
      }
      // Close drawer after operations
      this.drawerVisible = false;
      this.$emit(COMPONENT_EVENTS.REFRESH);
    },
    showFileLimitError(msg) {
      this.$store.commit('sbError', msg);
    },
    reset() {
      // Clear all local file and remark data
      this.localFileuplaoded = [];
      this.localFileremark = '';
      this.localUploadWithoutFile = false;
      this.files = {};
      this.filesbulk = {};
      this.dataArray = [];
      this.selectItem = [];
      this.bulkfilesloading = 0;
      this.clearuploadfiledata = false;
      this.sendForReviewDialogbulk = false;
      this.bulkworkpaperupload = false;
      this.log = 0;
      this.loading = false;
      this.localResetFlag = !this.localResetFlag;
      // Optionally emit to parent if you want parent to reset too
      this.$emit(COMPONENT_EVENTS.RESET);
    },
    async sendForReview() {
      if (this.sendforreviewloading) return false;
      this.sendforreviewloading = true;
      try {
        // Guard: this method only used when 'Upload without document' checkbox is selected
        if (this.filesOnly) {
          return false; // filesOnly mode shouldn't route here
        }
        if (!this.localUploadWithoutFile) {
          // Not in remark-only mode; nothing to do
            return false;
        }
        if (!this.localFileremark) {
          this.$store.commit('sbError', 'Remark is mandatory when uploading without a document.');
          return false;
        }
        // Determine if remark-only submit should refresh as submitted
        const willAutoSubmit = this.autoSubmitOnUpload
          && this.showUploadAndSubmit
          && !this.filesOnly
          && this.localUploadWithoutFile
          && this.task
          && this.task.stage !== 3;
        const fileuploadstatus = await this.$store.dispatch('tasks/sendForReview', {
          id: this.id,
          viewtaskdata: this.task,
          remark: this.localFileremark,
          remarkOnly: !this.autoSubmitOnUpload,
          isFileValid: this.isFileValid,
          suppressSnackbar: false
        });
        if (fileuploadstatus && fileuploadstatus.success) {
          if (willAutoSubmit) {
            this.$emit(COMPONENT_EVENTS.TASK_SUBMITTED);
          }
          this.drawerVisible = false;
          this.$emit(COMPONENT_EVENTS.REFRESH);
          return true;
        } else {
          const reviewError = fileuploadstatus && fileuploadstatus.error;
          this.$store.commit('sbError', reviewError || 'Error Adding Remarks.');
          return false;
        }
      } finally {
        this.sendforreviewloading = false;
      }
    },
    async sendForReviewBulk() {
      if (this.sendforreviewloadingbulk) return;
      this.sendforreviewloadingbulk = true;
      try {
        if (!this.localFileremark) {
          this.$store.commit('sbError', 'Remark is mandatory when uploading without a document.');
          return;
        }
        const remarkOnly = !this.autoSubmitOnUpload;
        const saveResult = await this.$store.dispatch('tasks/sendForReviewBulk', {
          id: this.id,
          selectItem: this.selectItem,
          localFileremark: this.localFileremark,
          remarkOnly,
          viewtaskdata: this.task,
          isFileValid: this.isFileValid,
          suppressSnackbar: true,
        });
        if (!saveResult?.success) return;

        if (remarkOnly) {
          this.finishBulkSaveOnlyFlow({ isRemarkOnly: true });
          return;
        }
        this.$store.commit('sbSuccess', BULK_UPLOAD_MESSAGES.SENT_FOR_REVIEW);
        this.finishBulkUploadFlow();
      } finally {
        this.sendforreviewloadingbulk = false;
      }
    },
    FilesUpload(filesss) {
      // Always treat filesss as an array
      const filesArray = Array.isArray(filesss) ? filesss : [filesss];
      let invalidExt = false;
      let invalidFiles = [];
      let invalidNames = [];
      // Clone currently selected local files
      let mergedFiles = [...this.localFileuplaoded];
      // Track only duplicates against already-uploaded files for user warning.
      // Local re-selections (same file picked again before confirming) will be silently replaced without warning.
      const existingDuplicateNames = []; // duplicates of already uploaded files
      // Collect already uploaded filenames (single or bulk) - case-insensitive
      const existingUploadedNames = (() => {
        const extract = task => Array.isArray(task?.upload_v2) ? task.upload_v2.map(f => (f.file_name || f.modifiedname || '').trim()) : [];
        let all = [];
        if (this.bulkworkpaper) {
          (this.selectItem || []).forEach(t => { all.push(...extract(t)); });
        } else {
          all = extract(this.task);
        }
        return Array.from(new Set(all.filter(Boolean).map(n => n.toLowerCase())));
      })();
      filesArray.forEach(file => {
        if (!file || !file.name) return;
        const name = file.name;
        const norm = name.toLowerCase().trim();
        // Validate name
        if (this.$nova && this.$nova.isValidFileName && !this.$nova.isValidFileName(name)) {
          invalidNames.push(name);
        }
        const ext = name.split('.').pop().toLowerCase();
        if (!this.ALLOWED_EXTENSIONS.includes(ext)) {
          invalidExt = true;
          invalidFiles.push(name);
        }
        // Local duplicate replace
        const localIdx = mergedFiles.findIndex(f => f.name && f.name.toLowerCase() === norm);
        if (localIdx !== -1) {
          // Silently replace previously selected (not yet uploaded) file with same name
          mergedFiles.splice(localIdx, 1, file);
          return; // do not flag as duplicate for user-facing warning
        }
        // Already uploaded duplicate (warn & allow replacement upload)
        if (existingUploadedNames.includes(norm)) {
          existingDuplicateNames.push(name);
          mergedFiles.push(file);
          return;
        }
        mergedFiles.push(file);
      });
      // Compute total size of final local selection
      const totalSize = mergedFiles.reduce((sum, f) => sum + (f.size || 0), 0);
      const uploadErrors = [];
      if (invalidNames.length > 0) {
        uploadErrors.push(
          `Invalid file name(s): ${invalidNames.join(', ')}. Only letters, numbers, spaces, underscores (_), hyphens (-), dots (.), or parentheses () are allowed.`
        );
      }
      if (invalidExt) {
        uploadErrors.push(
          `Only the following file types are allowed: ${this.ALLOWED_EXTENSIONS.join(', ')}. Invalid: ${invalidFiles.join(', ')}`
        );
      }
      if (totalSize > this.MAX_TOTAL_SIZE) {
        uploadErrors.push(
          `Total file size exceeds the limit (${(this.MAX_TOTAL_SIZE / (1024 * 1024)).toFixed(2)} MB)`
        );
      }
      if (uploadErrors.length > 0) {
        this.$store.commit('sbError', uploadErrors.join(' '));
        return;
      }
      // Only warn user when a file name matches an already uploaded file (server-side duplicate)
      if (existingDuplicateNames.length > 0) {
        this.$store.commit('sbWarning', `These file(s) are already uploaded and will be replaced: ${existingDuplicateNames.join(', ')}`);
      }
      if (mergedFiles.length > MAX_FILES_PER_UPLOAD) {
        this.localFileuplaoded = mergedFiles;
        this.uploadLimitExceeded = true;
        return;
      }
      this.uploadLimitExceeded = false;
      if (this.bulkuploadtemplate) {
        this.file = filesArray[0];
        this.readFile("file");
      } else {
        this.localFileuplaoded = mergedFiles;
      }
    },
    async uploadFiles() {
      this.sendforreviewloading = true;
      try {
        await this.$store.dispatch('tasks/uploadFiles', {
          id: this.id,
          files: this.localFileuplaoded,
          taskData: this.task,
          projectId: this.id,
          upload_v2: this.task.upload_v2,
          isFileValid: this.isFileValid,
          remark: this.localFileremark
        });
        // Clear files after successful upload
        this.localFileuplaoded = [];
        this.localResetFlag = !this.localResetFlag; // Force DragDropfile to clear UI
        return true;
      } finally {
        this.sendforreviewloading = false;
      }
    },
    // Internal helper to support suppressing snackbar when auto-submitting
    async uploadFilesInternal(suppressSnackbar) {
      this.sendforreviewloading = true;
      try {
        await this.$store.dispatch('tasks/uploadFiles', {
          id: this.id,
          files: this.localFileuplaoded,
          taskData: this.task,
          projectId: this.id,
          upload_v2: this.task.upload_v2,
          isFileValid: this.isFileValid,
          suppressSnackbar,
          remark: this.localFileremark
        });
        this.localFileuplaoded = [];
        this.localResetFlag = !this.localResetFlag;
        return true;
      } finally {
        this.sendforreviewloading = false;
      }
    },
    async uploadFilesBulk() {
      this.sendforreviewloadingbulk = true;
      try {
        const newFilesCount = Array.isArray(this.localFileuplaoded) ? this.localFileuplaoded.length : 0;
        const result = await this.$store.dispatch('tasks/uploadFilesBulk', {
          files: this.localFileuplaoded,
          id: this.id,
          selectItem: this.selectItem,
          viewtaskdata: this.task,
          isFileValid: this.isFileValid,
          remark: this.localFileremark,
          suppressSnackbar: true,
        });
        if (!result?.success) return;

        await this.handleBulkPostSave(newFilesCount, { isRemarkOnly: false });
      } finally {
        this.sendforreviewloadingbulk = false;
      }
    },
    removeAllInvalidFiles() {
      this.localFileuplaoded = this.localFileuplaoded.filter(f => this.isFileValid(f));
      this.FilesUpload(this.localFileuplaoded);
    },
    isFileValid(file) {
      if (!file.name) return false;
      const validName = this.$nova && this.$nova.isValidFileName ? this.$nova.isValidFileName(file.name) : true;
      const ext = file.name.split('.').pop().toLowerCase();
      const validExt = this.ALLOWED_EXTENSIONS.includes(ext);
      return validName && validExt;
    },
  },
}
</script>